/**
 * Every word a held, blocked or attested publish says — Story 25.4.
 *
 * Kept apart by WHERE EACH WORD COMES FROM, as `answer-mode-copy.ts` does,
 * because each kind is held to a different source by `tests/publish-copy.test.ts`:
 *
 *   DRAWN     a capture draws it — `editor-build`'s `publish-held`,
 *             `publish-blocked` and `publish-attest`, and `funnels-list`'s
 *             `moderation`. Each function returns the drawn sentence when given
 *             the drawn fiction (a card number; a job; Redwood Civil), and the
 *             test calls it with exactly that.
 *   COMPOSED  no capture draws it: the three other verticals, the three other
 *             ways a funnel can be held, and a funnel whose earlier version is
 *             live. Built on the drawn sentence's own shape, and listed here so a
 *             round-trip replaces them in one place.
 *
 * NO RUNTIME IMPORTS, so a node-side test can load this file without resolving
 * the workspace. The scan's names are repeated as string unions for that reason;
 * `publish-outcome.ts` narrows what the API sends to them.
 */

export type Intent = 'password' | 'payment_card' | 'government_id'
export type Vertical = 'employment' | 'credit' | 'insurance' | 'tenancy'

/** "a, b and c" — the drawn sentences' own way of listing. */
export function listed(words: readonly string[], last = 'and'): string {
  if (words.length <= 1) return words[0] ?? ''
  return `${words.slice(0, -1).join(', ')} ${last} ${words.at(-1)}`
}

/** What a held funnel asks for. `payment_card` is drawn; the others follow it. */
const ASKS: Readonly<Record<Intent, string>> = {
  password: 'a password',
  payment_card: 'a card number',
  government_id: 'an ID number',
}

/**
 * A regulated vertical, four ways. `employment` is drawn in all four places —
 * the blocked dialog, the attestation and the blocked card — and the other three
 * are composed on its pattern.
 */
const VERTICAL: Readonly<
  Record<Vertical, { screens: string; decides: string; regulated: string; screen: string }>
> = {
  employment: {
    screens: 'a job',
    decides: 'who gets the role',
    regulated: 'Hiring decisions are regulated',
    screen: 'job applicants',
  },
  credit: {
    screens: 'credit',
    decides: 'who gets credit',
    regulated: 'Lending decisions are regulated',
    screen: 'people applying for credit',
  },
  insurance: {
    screens: 'insurance',
    decides: 'who is insured',
    regulated: 'Insurance decisions are regulated',
    screen: 'people applying for insurance',
  },
  tenancy: {
    screens: 'a home to rent',
    decides: 'who gets the home',
    regulated: 'Letting decisions are regulated',
    screen: 'people applying to rent a home',
  },
}

const asks = (intents: readonly Intent[]): string => listed(intents.map((i) => ASKS[i]))
const screens = (verticals: readonly Vertical[]): string => listed(verticals.map((v) => VERTICAL[v].screens))
const decides = (verticals: readonly Vertical[]): string =>
  verticals.length === 1 ? VERTICAL[verticals[0]!].decides : `who gets ${listed(verticals.map((v) => VERTICAL[v].screens), 'or')}`
const regulated = (verticals: readonly Vertical[]): string =>
  verticals.length === 1 ? VERTICAL[verticals[0]!].regulated : 'Decisions like these are regulated'
const screen = (verticals: readonly Vertical[]): string => listed(verticals.map((v) => VERTICAL[v].screen))

export const DRAWN = {
  /* ── editor-build · publish-held ─────────────────────────────────────── */
  heldTitle: (name: string): string => `${name} is waiting for review`,
  heldLine: (intents: readonly Intent[]): string =>
    `It asks for ${asks(intents)}, so it can't go live until someone at Leadgible has looked at it. ` +
    "Forms on the Free and Starter plans can't ask for passwords, card numbers or ID numbers without a review.",
  heldNext: "We'll email you when it has been reviewed. If you'd rather not wait, remove the question and publish again.",
  close: 'Close',

  /* ── editor-build · publish-blocked ──────────────────────────────────── */
  blockedTitle: (name: string): string => `${name} can't be published on this plan`,
  blockedLine: (verticals: readonly Vertical[]): string =>
    `It screens people for ${screens(verticals)}. ` +
    "Funnels on the Free and Starter plans can't decide who gets a job, credit, insurance or a home.",
  blockedNext: (verticals: readonly Vertical[]): string =>
    'On Pro and above you can publish it after confirming your own compliance basis. ' +
    `Or change the questions so it isn't deciding ${decides(verticals)}.`,
  keepEditing: 'Keep editing',
  seePlans: 'See plans',

  /* ── editor-build · publish-attest ───────────────────────────────────── */
  attestTitle: 'Confirm your compliance basis to publish',
  attestLine: (name: string, verticals: readonly Vertical[]): string =>
    `${name} screens people for ${screens(verticals)}. ${regulated(verticals)}, and you are responsible for making them lawfully.`,
  attestBox: (org: string, verticals: readonly Vertical[]): string =>
    `I confirm that ${org} has its own legal basis for using this funnel to screen ${screen(verticals)}, ` +
    'and tells applicants how their answers are used.',
  attestRecord: (person: string, when: string): string => `We record this with your name and the time — ${person}, ${when}.`,
  notYet: 'Not yet',
  confirmAndPublish: 'Confirm and publish',

  /* ── the evidence row, in all three ──────────────────────────────────── */
  step: (number: number, title: string): string => `Step ${number} · ${title}`,
  quoted: (text: string): string => `"${text}"`,

  /* ── funnels-list · moderation ───────────────────────────────────────── */
  cardHeld: 'Waiting for review before it can publish',
  cardHeldLive: 'Your latest changes are waiting for review. The version already live keeps taking submissions.',
  cardBlockedChip: "Can't publish",
  cardBlocked: (verticals: readonly Vertical[]): string =>
    `It screens people for ${screens(verticals)}, which funnels on the Free and Starter plans can't do.`,
  openTheFunnel: 'Open the funnel',
} as const

export const COMPOSED = {
  /**
   * The dialogs, when an earlier version is live: a held or blocked REPUBLISH
   * leaves it serving (Story 25.3), and the API's own refusal says so. The
   * sentence is `funnels-list`'s, which draws that case on the card.
   */
  stillLive: 'The version already live keeps taking submissions.',
  /** The blocked card whose earlier version is live — the card keeps Published. */
  cardBlockedLive: (verticals: readonly Vertical[]): string =>
    `Your latest changes screen people for ${screens(verticals)}, which funnels on the Free and Starter plans can't publish. ` +
    'The version already live keeps taking submissions.',
  /** A finding with no step — the funnel's name, an outcome — named by where it was read. */
  where: {
    funnel_name: "The funnel's name",
    outcome: 'An outcome',
    disqualify_reason: "A disqualifier's reason",
    voice: 'The voice agent',
  } as Readonly<Record<string, string>>,
  /** Any other place with no step. */
  elsewhere: 'In the funnel',
} as const
